/**
 * Serviço para operações relacionadas a programação de atendimento
 * Migrado de App.js para estrutura modular
 */

// Desenvolvimento: localhost:3002. Produção: mesma origem (Apache proxy encaminha /api)
const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3002';
  }
  return window.location.origin;
};

const API_BASE_URL = getApiBaseUrl();

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Organiza áreas e técnicos em uma estrutura hierárquica.
 *
 * Se vínculos de coordenador forem fornecidos e houver pelo menos um registro
 * em `areaCoordLinks`, filtra apenas áreas vinculadas a coordenadores.
 * Caso contrário, utiliza todas as áreas disponíveis (comportamento legado),
 * garantindo que a sidebar de programação continue exibindo dados mesmo
 * quando a configuração de coordenadores ainda não estiver pronta.
 */
const organizeAreasAndTechnicians = (areas, technicians, areaTeamLinks, areaCoordLinks) => {
  if (!areas || !technicians || !areaTeamLinks) return [];

  let baseAreas = areas;

  // Quando vínculos de coordenador existem, filtrar apenas essas áreas
  if (Array.isArray(areaCoordLinks) && areaCoordLinks.length > 0) {
    const areasWithCoordinator = new Set(
      areaCoordLinks.map(link => link.id_area)
    );
    baseAreas = areas.filter(area => areasWithCoordinator.has(area.id));
  }

  const organized = baseAreas.map(area => {
    const areaTechnicians = areaTeamLinks
      .filter(link => link.id_area === area.id)
      .map(link => {
        const technician = technicians.find(tech => tech.id === link.id_tech || tech.codigo === link.id_tech);
        return technician ? { ...technician, areaId: area.id } : null;
      })
      .filter(tech => tech !== null);
    
    return {
      ...area,
      tecnicos: areaTechnicians
    };
  }).filter(area => area.tecnicos && area.tecnicos.length > 0);
  
  return organized;
};

/**
 * Carrega dados para programação (áreas, técnicos e vínculos).
 *
 * Se `equipmentModelFilter` tiver `idBrand` e `idSubgroup` (TB01010_MARCA / TB01010_SUBGRUPO),
 * restringe técnicos aos que possuem registro em LCMY_REG_MODELS_TEC para essa combinação.
 */
export const loadScheduleData = async (equipmentModelFilter = null) => {
  try {
    const idBrand = equipmentModelFilter?.idBrand != null
      ? String(equipmentModelFilter.idBrand).trim()
      : '';
    const idSubgroup = equipmentModelFilter?.idSubgroup != null
      ? String(equipmentModelFilter.idSubgroup).trim()
      : '';

    const [areasRes, techniciansRes, areaTeamRes] = await Promise.all([
      fetch(`${API_BASE_URL}/api/team/areas`, { headers: getAuthHeaders() }),
      fetch(`${API_BASE_URL}/api/team/technicians`, { headers: getAuthHeaders() }),
      fetch(`${API_BASE_URL}/api/team/areateam`, { headers: getAuthHeaders() })
    ]);

    const [areasData, techniciansData, areaTeamData] = await Promise.all([
      areasRes.json(),
      techniciansRes.json(),
      areaTeamRes.json()
    ]);

    if (areasData.success && techniciansData.success && areaTeamData.success) {
      let technicians = techniciansData.data;
      let areaTeamLinks = areaTeamData.data;

      if (idBrand && idSubgroup) {
        try {
          const q = new URLSearchParams({ id_brand: idBrand, id_subgroup: idSubgroup });
          const modelRes = await fetch(
            `${API_BASE_URL}/api/team/technicians-for-equipment-model?${q}`,
            { headers: getAuthHeaders() }
          );
          const modelJson = await modelRes.json();
          // Regra:
          // - Havendo vínculos para marca+subgrupo: restringe técnicos por especialidade.
          // - Sem vínculos (array vazio): fallback para comportamento legado (todos os técnicos da área).
          if (modelJson.success && Array.isArray(modelJson.data) && modelJson.data.length > 0) {
            const allowed = new Set(
              modelJson.data.map((id) => String(id || '').trim()).filter(Boolean)
            );
            const norm = (v) => String(v || '').trim();
            technicians = technicians.filter((t) => allowed.has(norm(t.id)));
            areaTeamLinks = areaTeamLinks.filter((link) => allowed.has(norm(link.id_tech)));
          }
        } catch (modelErr) {
          console.error('Erro ao filtrar técnicos por modelo do equipamento:', modelErr);
        }
      }

      const organized = organizeAreasAndTechnicians(
        areasData.data,
        technicians,
        areaTeamLinks,
        null
      );
      return { success: true, data: organized };
    }

    return { success: false, data: [] };
  } catch (error) {
    console.error('Erro ao carregar dados para programação:', error);
    return { success: false, data: [], error: error.message };
  }
};

function padCodTec(value) {
  const raw = value != null ? String(value).trim() : '';
  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw;
  return digits.padStart(4, '0').slice(-4);
}

export const fetchAgendaNearbyTechs = async ({ address, day, km, city }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/os/nearby-techs`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ address, day, km, city: city || '' }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      return { success: false, originFound: false, origin: null, counts: {}, points: [], error: data.message || 'Falha na busca' };
    }
    const map = {};
    (data.counts || []).forEach((row) => {
      const n = Number(row.count) || 0;
      if (!n) return;
      const raw = String(row.codTec || '').trim();
      const padded = padCodTec(raw);
      const stripped = raw.replace(/\D/g, '').replace(/^0+/, '') || padded;
      [raw, padded, stripped].forEach((k) => { if (k) map[k] = n; });
    });
    const origin = data.origin && Number.isFinite(Number(data.origin.lat)) && Number.isFinite(Number(data.origin.lng))
      ? { lat: Number(data.origin.lat), lng: Number(data.origin.lng) }
      : null;
    const points = (data.points || []).map((row) => ({
      os: String(row.os || '').trim(),
      codTec: padCodTec(row.codTec),
      cliente: String(row.cliente || '').trim(),
      bairro: String(row.bairro || '').trim(),
      defeitoAlert: String(row.defeitoAlert || row.defeito_alert || '').trim(),
      colorBg: String(row.colorBg || row.color_bg || '').trim(),
      preventiva: String(row.preventiva || row.TB02115_PREVENTIVA || '').trim(),
      day: String(row.day || '').trim() || undefined,
      lat: Number(row.lat),
      lng: Number(row.lng),
      km: Number(row.km) || 0,
    })).filter((p) => p.os && Number.isFinite(p.lat) && Number.isFinite(p.lng));
    return { success: true, originFound: !!data.originFound && !!origin, origin, counts: map, points };
  } catch (error) {
    return { success: false, originFound: false, origin: null, counts: {}, points: [], error: error.message };
  }
};

export const fetchAgendaMapsKey = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/maps/google-browser-key`, {
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    return data && data.success && data.key ? data.key : null;
  } catch {
    return null;
  }
};

export const fetchAgendaMonitoredLocations = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/os/agenda-monitored-locations`, {
      headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      return { success: false, premiumRequired: false, technicians: [], error: data.message || 'Falha ao carregar técnicos monitorados' };
    }
    const technicians = (data.technicians || []).map((row) => ({
      id: String(row.id || '').trim(),
      nome: String(row.nome || '').trim(),
      iniciais: String(row.iniciais || '').trim(),
      status: String(row.status || '').trim(),
      lat: Number(row.lat),
      lng: Number(row.lng),
      speedKmh: row.speedKmh != null ? Number(row.speedKmh) : null,
      updatedAt: row.updatedAt || null,
    })).filter((t) => t.id && Number.isFinite(t.lat) && Number.isFinite(t.lng));
    return {
      success: true,
      premiumRequired: !!data.premiumRequired,
      technicians,
    };
  } catch (error) {
    return { success: false, premiumRequired: false, technicians: [], error: error.message };
  }
};

export { padCodTec as padAgendaCodTec };

